<script lang="ts" setup>
import type { PropType } from 'vue'
import OverlayBase from '@/components/overlays/OverlayBase.vue'
import OverlayHeader from '@/components/overlays/OverlayHeader.vue'
import SkrButton from '@/components/button/SkrButton.vue'
import SignersTable from '@/components/signature-request/SignersTable.vue'

const props = defineProps({
  isSealing: Boolean,
  // Apparently, this may be set by an external consumer,
  // so we need to be aware of it
  isSimpleUi: Boolean,
  processedRequests: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  showHeader: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()

const exitUrlStore = useExitUrlStore()

const { directSignSession } = storeToRefs(useDirectSignStore())
const { exitDelay } = storeToRefs(exitUrlStore)
const { areExitUrlsApplicable, leaveToExitUrl, initAutoRedirect } = exitUrlStore
const { smAndUp, smAndDown } = useDisplay()

const isSingleRequest = ref(props.processedRequests?.length === 1)
const signatureRequestData = ref<SignatureRequestData | null>(null)

const invitingBusinessId = computed(() => signatureRequestData.value?.businessId ?? '')
const { rootStyle } = useIdentificationTheme(invitingBusinessId)

const title = computed(() => {
  return props.isSealing ? t('seal.success.title') : t('sign.success.title')
})

const onClose = async () => {
  exitUrlStore.cancelRedirect()
  await navigateTo({ name: 'index', query: { tab: 'inbox', refresh: 'true' } })
  emit('close')
}

const { srRepository, directSrRepository } = useLegacyApi()

const updateSignatureRequestData = async () => {
  signatureRequestData.value = directSignSession.value
    ? await directSrRepository.get(props.processedRequests[0])
    : await srRepository.get(props.processedRequests[0])
}

watchEffect(() => {
  if (signatureRequestData.value?.id && isSingleRequest.value) {
    initAutoRedirect(signatureRequestData.value.id)
  }
})

onMounted(() => {
  if (isSingleRequest.value) {
    void updateSignatureRequestData()
  }
})

onBeforeUnmount(() => {
  exitUrlStore.cancelRedirect()
})
</script>

<template>
  <overlay-base data-cy="overlay_signing_success" :transition="false" :show-header="showHeader">
    <template #header>
      <overlay-header @close="onClose" />
    </template>
    <div class="identification-typography" :style="rootStyle">
      <skr-spinner v-if="isSingleRequest && !signatureRequestData" fullscreen />
      <v-container v-else class="pb-16 ma-auto">
        <v-col class="ma-auto pa-0" cols="12" sm="6" xl="4">
          <div class="text-center">
            <nuxt-img width="153" height="163" :src="`/img/sr-invited.png`" class="mt-1 ml-8" alt="" />
            <div class="text-center my-8 pb-6">
              <skr-heading level="1" class="ma-0" data-cy="overlay_title" semantic>
                {{ title }}
              </skr-heading>
            </div>
          </div>

          <div class="actions mb-12 text-center">
            <template
              v-if="isSingleRequest && signatureRequestData && areExitUrlsApplicable([signatureRequestData.id])"
            >
              <skr-button
                type="primary"
                color="primary-xqes"
                size="xl"
                class="mb-4"
                data-cy="next_button"
                @click="leaveToExitUrl"
              >
                {{ t('global.next') }}
              </skr-button>
              <div class="text-center" :class="{ 'mt-4': smAndUp }" data-cy="redirect_message">
                {{
                  t('signature_request.success.automatic_redirection', {
                    n: exitDelay,
                  })
                }}
              </div>
            </template>

            <template v-else-if="isSingleRequest">
              <skr-button
                data-cy="view_document_button"
                type="secondary"
                color="primary-xqes"
                size="xl"
                :block="smAndDown"
                @click="emit('close')"
              >
                {{ $t('global.view_request') }}
              </skr-button>
            </template>

            <template v-else>
              <skr-button type="primary" color="primary-xqes" size="xl" data-cy="back_to_button" @click="onClose">
                {{ t('global.back_to_documents') }}
              </skr-button>
            </template>
          </div>
        </v-col>

        <v-col
          v-if="isSingleRequest && signatureRequestData"
          class="ma-auto mt-4"
          cols="12"
          sm="6"
          xl="4"
          data-cy="sr_signers"
        >
          <signers-table :signature-request-data="signatureRequestData" />
        </v-col>
      </v-container>
    </div>
  </overlay-base>
</template>

<style lang="scss" scoped>
.actions {
  @include max-width(sm) {
    width: 100%;
    flex-direction: column;
    .v-btn {
      width: 100%;
    }
  }
  @include min-width(sm) {
    align-items: stretch;
  }
}
</style>
